import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PageControl } from '../model/page-control';
import { environment } from '@env/environment';
import { Observable, of, timer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { DefaultStatus, DefaultStatusMessage } from '@app-model/login';
import { Client, ClientPageableResponse, FileUploader, Plan, SimplifiedClientHelper } from '@app-model/client';
import { IdName } from '@app-model/application';
import { PromotedContent } from '@app-model/promotedContent';
import { IntegrationConfigHelper } from '@app-model/product';
import { ApiKeyCreatedRequest, ApiKeyCreatedResponse, ApiKeyPageResponse, ReportFrequency } from '@app-model/api-key';

@Injectable({
	providedIn: 'root'
})
export class ClientService {
	constructor(private http: HttpClient, private toastr: ToastrService) {}

	paginate(pageControl: PageControl, searchTerm: string): Observable<ClientPageableResponse> {
		const body: any = {
			page: pageControl.page,
			pageSize: pageControl.limit,
			sortDirection: pageControl.order,
			sortField: pageControl.orderField
		};

		const params = {};
		if (searchTerm) {
			params['query'] = searchTerm;
		}

		if (pageControl.param && pageControl.param !== '' && pageControl.value && pageControl.value !== '') {
			body.param = pageControl.param;
			body.value = pageControl.value;
		}
		return this.http
			.post(`${environment.api}/client/list`, body, { params })
			.pipe(map((response: ClientPageableResponse) => response));
	}

	get(id: string): Observable<Client> {
		return this.http.get<Client>(`${environment.api}/client/${id}`);
	}

	getClientOptions(): Observable<IdName[]> {
		return this.http.get<IdName[]>(`${environment.api}/client/options`);
	}

	getSimplifiedClient(id: string): Observable<SimplifiedClientHelper> {
		return this.http.get<SimplifiedClientHelper>(`${environment.api}/client/simplified/${id}`);
	}

	getPromotedContents(clientId: string): Observable<PromotedContent[]> {
		return this.http.get<PromotedContent[]>(`${environment.api}/client/promotedContents/${clientId}`);
	}

	savePromotedContents(clientId: string, promotedContents: PromotedContent[]): Observable<PromotedContent[]> {
		return this.http.post<PromotedContent[]>(
			`${environment.api}/client/savePromotedContents/${clientId}`,
			promotedContents
		);
	}

	uploadBannerImage(file: File, clientId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/client/uploadPromotionBanner/${clientId}`, body);
	}

	uploadImageGridImage(file: File, clientId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/client/uploadPromotionGrid/${clientId}`, body);
	}

	uploadCarrouselImage(file: File, clientId: string, fileType: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(
			`${environment.api}/client/uploadPromotionCarrousel/${clientId}/${fileType}`,
			body
		);
	}

	uploadPdf(file: File, clientId: string): Observable<FileUploader> {
		const body = new FormData();
		body.append('file', file);
		return this.http.post<FileUploader>(`${environment.api}/client/uploadPdf/${clientId}`, body);
	}

	remove(id: string): Observable<DefaultStatus> {
		return this.http.post<DefaultStatus>(`${environment.api}/client/remove/${id}`, {});
	}

	syncConfig(clientId: string, configId: string): Observable<DefaultStatus> {
		return this.http.post<DefaultStatus>(`${environment.api}/client/runIntegrationSync/${clientId}/${configId}`, {});
	}

	getAvailablePlans(clientId?: string): Observable<Plan[]> {
		return this.http.get<Plan[]>(`${environment.api}/client/availablePlans/${clientId ? clientId : ''}`);
	}

	changePlan(clientId: string, planId: string): Observable<DefaultStatus> {
		return this.http.post<DefaultStatus>(
			`${environment.api}/client/changePlan/${clientId ? clientId + '/' : ''}${planId}`,
			{}
		);
	}

	getCurrentPlan(clientId: string): Observable<Plan> {
		return this.http.get<Plan>(`${environment.api}/client/currentPlan/${clientId}`);
	}

	getPlanByPlanId(planId: string): Observable<Plan> {
		return this.http.get<Plan>(`${environment.api}/client/plans/${planId}`);
	}

	currentPlanIsPaid(clientId: string): Observable<boolean> {
		return this.getCurrentPlan(clientId).pipe(
			map(plan => {
				return !!plan && Number(plan?.price?.amountCents) > 0;
			}),
			catchError(err => {
				return of(false);
			})
		);
	}

	validateIntegrationConfig(integrationConfigHelper: IntegrationConfigHelper): Observable<DefaultStatusMessage> {
		return this.http.post<DefaultStatusMessage>(
			`${environment.api}/client/validate-integration-config`,
			integrationConfigHelper
		);
	}

	listApiKeys(clientId: string, pageControl: PageControl): Observable<ApiKeyPageResponse> {
		const body = {
			page: pageControl.page,
			pageSize: pageControl.limit,
			sortDirection: pageControl.order,
			sortField: pageControl.orderField
		};
		return this.http.post<ApiKeyPageResponse>(`${environment.api}/client/${clientId}/apikeys/list`, body);
	}

	createApiKey(clientId: string, request: ApiKeyCreatedRequest): Observable<ApiKeyCreatedResponse> {
		return this.http.post<ApiKeyCreatedResponse>(`${environment.api}/client/${clientId}/apikeys`, request);
	}

	revokeApiKey(clientId: string, prefix: string): Observable<DefaultStatusMessage> {
		const encodedClientId = encodeURIComponent(clientId);
		const encodedPrefix = encodeURIComponent(prefix);
		return this.http.post<DefaultStatusMessage>(
			`${environment.api}/client/revokeApiKey/${encodedClientId}/${encodedPrefix}`,
			{}
		);
	}

	triggerReportManually(frequency: ReportFrequency, force: boolean = false): Observable<DefaultStatusMessage> {
		return this.http.post<DefaultStatusMessage>(
			`${environment.api}/admin/test-reports/${frequency}`,
			{},
			{ params: { force: force.toString() } }
		);
	}
}
